import { FC, type JSX } from "react";
import { useTranslation } from "react-i18next";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import ErrorIcon from "@mui/icons-material/Error";
import CircleIcon from "@mui/icons-material/Circle";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import type { Theme } from "@mui/material/styles";

import type { ComplianceStatus } from "src/services/certificateStatus";

// ─── Primitive icons (color baked in) ─────────────────────────────────────────

export const OkIcon = () => <CheckCircleIcon htmlColor="green" />;
export const ExpiredIcon = () => <ErrorIcon htmlColor="red" />;
export const MissingIcon = () => <CircleIcon htmlColor="red" />;
export const BufferIcon = () => <AlarmOnIcon htmlColor="#ffbf00" />;
export const CoveredByParentIcon = () => <CircleIcon htmlColor="#9e9e9e" />;

// ─── Status → icon map ────────────────────────────────────────────────────────

const STATUS_ICON: Record<string, JSX.Element> = {
  ok: <OkIcon />,
  never_expires: <OkIcon />,
  expired: <ExpiredIcon />,
  missing: <MissingIcon />,
  buffer: <BufferIcon />,
};

const PLACEHOLDER = <div style={{ width: 20, height: 20 }} />;

// ─── Supervision ring ─────────────────────────────────────────────────────────

const supervisionSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  border: "3px solid #4254bc",
  boxShadow: "0 0 8px #4254bc",
  padding: "1px",
};

const supervisedLimitationSx = {
  ...supervisionSx,
  padding: 0,
};

const limitationSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  backgroundColor: (theme: Theme) => (theme.palette.mode === "dark" ? "#FFD700" : "#1A1A1A"),
  padding: "2px",
};

// ─── Main component ───────────────────────────────────────────────────────────

interface CourseStatusIconProps {
  status: ComplianceStatus | string | null | undefined;
  coveredByParent?: boolean;
  coveredByParentCourseName?: string;
  underSupervision?: boolean;
  hasLimitations?: boolean;
  showTooltip?: boolean;
}

const CourseStatusIcon: FC<CourseStatusIconProps> = ({
  status,
  coveredByParent = false,
  coveredByParentCourseName,
  underSupervision = false,
  hasLimitations = false,
  showTooltip = true,
}) => {
  const { t } = useTranslation();

  if (!status) return PLACEHOLDER;

  const baseIcon = coveredByParent ? <CoveredByParentIcon /> : (STATUS_ICON[status] ?? <OkIcon />);
  const icon = hasLimitations ? <Box sx={limitationSx}>{baseIcon}</Box> : baseIcon;
  const decoratedIcon = underSupervision ? (
    <Box sx={hasLimitations ? supervisedLimitationSx : supervisionSx}>{icon}</Box>
  ) : (
    icon
  );
  const tooltipItems = [
    coveredByParent
      ? t("certificate.covered_by_parent_tooltip", {
          courseName: coveredByParentCourseName ?? t("certificate.covered_by_parent_fallback_course"),
          interpolation: { escapeValue: false },
        })
      : null,
    underSupervision ? t("aircraft_management.under_supervision") : null,
    hasLimitations ? t("limitations.title") : null,
  ].filter((item): item is string => Boolean(item));
  const tooltipTitle = tooltipItems.length > 0 ? tooltipItems.join(" + ") : null;

  if (!tooltipTitle || !showTooltip) return decoratedIcon;

  return (
    <Tooltip title={tooltipTitle} placement="top" arrow>
      {underSupervision ? decoratedIcon : <span style={{ display: "inline-flex" }}>{decoratedIcon}</span>}
    </Tooltip>
  );
};

export default CourseStatusIcon;
